import getLogger from '@ignitesales/logger-es';
import { buildPageUrl, setRuleEvaluatorAnswers } from '../utils';
import {
  extractPageFromPath,
  extractAnswersFromPageResponse,
  extractProgressFromPageResponse,
  extractProductsFromPageResponse,
  extractValidationErrorsFromPageResponse,
} from '../state';

const logger = getLogger({ module: 'ignite' });
const serviceWaitTimeout = 30000;
let runMode = 'development';

const getRunMode = () => runMode;

const setRunMode = (newRunMode) => {
  logger.info('Setting runMode', newRunMode);
  runMode = newRunMode;
};

const isDetailsPage = () => {
  return extractPageFromPath(window.location.pathname).type === 'details';
};

const commonServicesInjected = () => {
  return (
    ignite &&
    ignite.answerService &&
    ignite.pageResolutionService &&
    ignite.contactService &&
    ignite.noteService &&
    ignite.appointmentService
  );
};

const branchServicesInjected = (isBranchGuide) => {
  if( isDetailsPage() || !isBranchGuide ) {
    logger.debug('No branch services needed');
  }
  return isDetailsPage() || !isBranchGuide || (ignite && ignite.employeeLoginService);
};

const servicesInitialized = () => {
  const waitStart = Date.now();
  return new Promise((resolve, reject) => {
    const checkForServices = () => {
      logger.info('checking service availability');
      if (commonServicesInjected()) {
        logger.info('services ready');
        return resolve(true);
      }

      const elapsed = Date.now() - waitStart;
      if (elapsed >= serviceWaitTimeout) {
        return reject(new Error('services unavailable'));
      }

      logger.info('waiting for service availability');
      setTimeout(() => checkForServices(), 200);
    };
    checkForServices();
  });
};

const loadInitialState = async (options = {}) => {
  await servicesInitialized();
  logger.info('loading initial state', options);

  // initialize user
  const initializedUser = await ignite.pageResolutionService.initializeUser();
  const { uuid, guideDelivery } = initializedUser;

  // determine if branch guide services were injected
  const isBranchGuide = guideDelivery.toLowerCase() === 'branch';
  if( branchServicesInjected(isBranchGuide) ) {
    logger.debug('All ignite services have been initialized for state setup');
  } else {
    logger.error('Unable to load all ignite services needed to support this guide type');
  }

  // load current page data
  const currentPageRequest = extractPageFromPath(window.location.pathname);
  const currentPageResponse = await ignite.pageResolutionService.loadPage({
    page: currentPageRequest,
  });
  const currentPageUrl = buildPageUrl({
    ...currentPageResponse.page,
    type: currentPageResponse.type,
    uuid,
  });
  const currentPageType = currentPageResponse.type;
  // get answers from page response and from local answer store
  const answers = extractAnswersFromPageResponse(currentPageResponse);
  const localAnswers = JSON.parse( window.sessionStorage.getItem(`${uuid}-localAnswers`) );
  setRuleEvaluatorAnswers(answers);
  // get products, progress, and page validations
  const progress = extractProgressFromPageResponse(currentPageResponse);
  const products = extractProductsFromPageResponse(currentPageResponse);
  const validationErrors = extractValidationErrorsFromPageResponse(currentPageResponse);

  /**
   * @typedef fgrgr
   */
  // noinspection UnnecessaryLocalVariableJS
  /**
   * @typedef NeuroStateInitObject
   * @property {Object} user - active user details
   * @property {string} currentPage - current page url
   * @property {Object} pages - guide pages that have been accessed
   * @property {Object} answers - conversation answers
   * @property {Object} products - recommended products
   * @property {Object} progress - guide completion progress
   * @property {Array} validationErrors - validation errors for current page
   * @property {Object} guideConfigurations - object with guide config details (i.e. isBranchGuide, isMockMode, etc)
   * @type NeuroStateInitObject
   */
  const initialState = {
    answers: {...localAnswers, ...answers },
    pages: {
      currentPageUrl,
      currentPageType,
      defaultPageTitle: document.title,
      progress,
      isAnimatingPages: false,
      isLoadingNextPage: false,
      savedPages: {
        [currentPageUrl]: currentPageResponse.page
      },
      validationErrors
    },
    products,
    user: { ...initializedUser },
    guideConfigurations: {
      isBranchGuide
    }
  };

  return initialState;
};

const ignite = {
  getRunMode,
  loadInitialState,
  servicesInitialized,
  setRunMode
};
// mm-web service should still trigger the legacy call for on branch guides `showEmployeeDialog`.
// we don't need it, just here for debugging login flow logic if needed (assurance that backend agrees we need to login)
ignite.showEmployeeDialog = params => {
  logger.debug('Deprecated employee login flow call triggered, neuro-app should handle this flow', params);
};
window.ignite = ignite;

export {
  ignite as default,
  ignite,
  servicesInitialized
};
